import { createActionHandler, createReducer } from '@pressreader/appstore';

import { Comment } from '../../types/comment';
import {
    commentDeleted,
    commentUpdated,
    loadNextPageBegan,
    loadNextPageDone,
    loadNextPageFailed,
    loadPreviousPageBegan,
    loadPreviousPageDone,
    loadPreviousPageFailed,
    loadRepliesBegan,
    loadRepliesDone,
    loadRepliesFailed,
    newCommentPosted,
    newReplyPosted,
    postCommentBegan,
    postCommentDone,
    postCommentFailed,
    userBanned,
    votesUpdated,
} from '../actions/comments';
import { followUserDone, initialLoadBegan, initialLoadDone, unfollowUserDone } from '../actions/common';

interface State {
    comments: Comment[];

    offset: number;
    totalCommentsCount: number;
    rootCommentsCount: number;

    isLoadingPreviousPage: boolean;
    isLoadingNextPage: boolean;

    isPostingDraftId: string | null;

    /** Used to reset root editor when new root comment (not reply) is posted. */
    newCommentId: string | null;
    /** Used to show quick share buttons on newly created comment or reply. */
    newPostId: string | null;
}

const initialState: State = {
    comments: [],

    offset: 0,
    totalCommentsCount: 0,
    rootCommentsCount: 0,

    isLoadingPreviousPage: false,
    isLoadingNextPage: false,

    isPostingDraftId: null,
    newCommentId: null,
    newPostId: null,
};

function updateComment(
    commentId: string,
    comments: Comment[],
    updater: (comment: Comment) => Comment,
): { comment: Comment | null; comments: Comment[] } {
    const idx = comments.findIndex(c => c.id === commentId);
    if (idx < 0) {
        return { comments, comment: null };
    }
    const comment = updater(comments[idx]);
    return { comments: [...comments.slice(0, idx), comment, ...comments.slice(idx + 1)], comment };
}

const reducer = createReducer(
    initialState,
    createActionHandler(initialLoadBegan, state => ({
        ...state,
        comments: [],
        offset: 0,
        totalCommentsCount: 0,
        newCommentId: null,
        newPostId: null,
        attachmentsByDraftId: {},
    })),
    createActionHandler(initialLoadDone, (state, { payload }) => ({
        ...state,
        comments: payload.comments,
        offset: payload.offset,
        totalCommentsCount: payload.totalCommentsCount,
        rootCommentsCount: payload.rootCommentsCount,
    })),
    createActionHandler(loadNextPageBegan, state => ({
        ...state,
        isLoadingNextPage: true,
    })),
    createActionHandler(loadNextPageDone, (state, { payload }) => ({
        ...state,
        comments: [...state.comments, ...payload.comments],
        totalCommentsCount: payload.totalCommentsCount,
        rootCommentsCount: payload.rootCommentsCount,
        isLoadingNextPage: false,
    })),
    createActionHandler(loadNextPageFailed, state => ({
        ...state,
        isLoadingNextPage: false,
    })),
    createActionHandler(loadPreviousPageBegan, state => ({
        ...state,
        isLoadingPreviousPage: true,
    })),
    createActionHandler(loadPreviousPageDone, (state, { payload }) => ({
        ...state,
        comments: [...payload.comments, ...state.comments],
        offset: Math.max(state.offset - payload.comments.filter(c => !c.parentId).length, 0),
        totalCommentsCount: payload.totalCommentsCount,
        rootCommentsCount: payload.rootCommentsCount,
        isLoadingPreviousPage: false,
    })),
    createActionHandler(loadPreviousPageFailed, state => ({
        ...state,
        isLoadingPreviousPage: false,
    })),
    createActionHandler(loadRepliesBegan, (state, { payload }) => {
        const { comments } = updateComment(payload.commentId, state.comments, comment => ({ ...comment, isLoadingReplies: true }));
        return comments === state.comments ? state : { ...state, comments };
    }),
    createActionHandler(loadRepliesDone, (state, { payload }) => {
        const { commentId, replies } = payload;

        let { comments } = updateComment(commentId, state.comments, comment => ({
            ...comment,
            repliesCount: payload.totalRepliesCount,
            isLoadingReplies: false,
            isReloadRepliesRequired: false,
        }));

        if (comments === state.comments) {
            // Comment with commentId is not found. This should not happen. Ignore if it does.
            return state;
        }

        if (payload.replace) {
            comments = comments.filter(c => c.parentId !== commentId);
        }
        let idx = -1;
        for (let i = comments.length - 1; i >= 0; --i) {
            if (comments[i].parentId === commentId || comments[i].id === commentId) {
                idx = i + 1;
                break;
            }
        }

        return {
            ...state,
            comments: idx >= 0 ? [...comments.slice(0, idx), ...replies, ...comments.slice(idx)] : comments,
        };
    }),
    createActionHandler(loadRepliesFailed, (state, { payload }) => {
        const { comments } = updateComment(payload.commentId, state.comments, comment => ({ ...comment, isLoadingReplies: false }));
        return comments === state.comments ? state : { ...state, comments };
    }),
    createActionHandler(postCommentBegan, (state, { payload }) => ({
        ...state,
        isPostingDraftId: payload.draftId,
    })),
    createActionHandler(postCommentDone, state => ({
        ...state,
        isPostingDraftId: null,
    })),
    createActionHandler(postCommentFailed, state => ({
        ...state,
        isPostingDraftId: null,
    })),
    createActionHandler(newReplyPosted, (state, { payload }) => {
        const { draftParentId, reply } = payload;
        let idx = -1;
        for (let i = state.comments.length - 1; i >= 0; --i) {
            const comment = state.comments[i];
            if (comment.parentId === reply.parentId || comment.id === reply.parentId) {
                idx = i + 1;
                break;
            }
        }

        if (idx < 0) {
            return state;
        }
        let comments = [...state.comments.slice(0, idx), reply, ...state.comments.slice(idx + 1)];
        // Update the parent this comment was intended for.
        comments = updateComment(draftParentId, comments, comment => ({
            ...comment,
        })).comments;
        // Update the actual parent.
        comments = updateComment(reply.parentId, comments, comment => ({
            ...comment,
            repliesCount: comment.repliesCount + 1,
            isReloadRepliesRequired: true,
        })).comments;
        return {
            ...state,
            comments,
            newPostId: reply.id,
        };
    }),
    createActionHandler(newCommentPosted, (state, { payload }) => ({
        ...state,
        comments: payload.comments,
        offset: payload.offset,
        rootCommentsCount: payload.rootCommentsCount,
        totalCommentsCount: payload.totalCommentsCount,
        newCommentId: payload.comment.id,
        newPostId: payload.comment.id,
    })),
    createActionHandler(commentDeleted, (state, { payload }) => {
        const { commentId } = payload;
        const { comments, comment } = updateComment(commentId, state.comments, comment => ({ ...comment, isDeleted: true }));
        if (comments === state.comments || !comment) {
            return state;
        }
        return {
            ...state,
            comments,
            rootCommentsCount: comment.parentId ? state.rootCommentsCount : state.rootCommentsCount - 1,
            totalCommentsCount: state.totalCommentsCount - 1,
        };
    }),
    createActionHandler(commentUpdated, (state, { payload }) => ({
        ...state,
        comments: updateComment(payload.comment.id, state.comments, () => payload.comment).comments,
        newPostId: payload.comment.id,
    })),
    createActionHandler(votesUpdated, (state, { payload }) => ({
        ...state,
        comments: updateComment(payload.commentId, state.comments, comment => ({
            ...comment,
            currentVote: payload.currentUserVote,
            likesCount: payload.likesCount,
            dislikesCount: payload.dislikesCount,
        })).comments,
    })),
    createActionHandler(userBanned, (state, { payload }) => {
        const { userProfileName } = payload;

        let totalDeletedCommentsCount = 0;
        let rootDeletedCommentsCount = 0;
        const comments = state.comments.map(c => {
            if (c.userProfile.profileName !== userProfileName) {
                return c;
            }
            ++totalDeletedCommentsCount;
            if (!c.parentId) {
                ++rootDeletedCommentsCount;
            }
            const comment: Comment = {
                ...c,
                isDeleted: true,
            };
            return comment;
        });
        if (!totalDeletedCommentsCount) {
            return state;
        }

        return {
            ...state,
            comments,
            totalCommentsCount: state.totalCommentsCount - totalDeletedCommentsCount,
            rootCommentsCount: state.rootCommentsCount - rootDeletedCommentsCount,
        };
    }),
    createActionHandler(followUserDone, (state, { payload }) => ({
        ...state,
        comments: state.comments.map(comment => {
            if (comment.userProfile.profileName !== payload.userProfileName) {
                return comment;
            }
            const result: Comment = {
                ...comment,
                userProfile: {
                    ...comment.userProfile,
                    isFollowedByCurrentUser: true,
                },
            };
            return result;
        }),
    })),
    createActionHandler(unfollowUserDone, (state, { payload }) => ({
        ...state,
        comments: state.comments.map(comment => {
            if (comment.userProfile.profileName !== payload.userProfileName) {
                return comment;
            }
            const result: Comment = {
                ...comment,
                userProfile: {
                    ...comment.userProfile,
                    isFollowedByCurrentUser: false,
                },
            };
            return result;
        }),
    })),
);

export { State, reducer };
